import type { LabeledGraphicFontSchemaType } from '@vedalib/editor/lib/brand'
import {
  CONFIG_ELEMENTS,
  type LabeledGraphicPoint,
  type LabeledGraphicElementValue,
} from '@vedalib/editor/lib/elements'
import { FileUsageTypeEnum } from '@vedalib/editor/lib/files'
import { rtValueToText } from '@vedalib/rich-text'
import cn from 'classnames'
import * as R from 'ramda'
import React, { useRef, useState } from 'react'

import DropdownCourse from 'components/Editor/EditorElements/courseKit/DropDownCourse'
import {
  COURSE_TOOLTIP,
  COURSE_TOOLTIP_TEST_MATCHING,
} from 'components/Editor/EditorElements/courseKit/portalsCurseKit'
import Icon from 'components/uiKit/Icon'
import type { ElementFontCss } from 'services/Branding/types'
import { AppMode, PreviewMode } from 'services/Store/Project/enums'
import { useGetEntityAnalyticsConfig } from 'services/lms/hooks/useGetEntityAnalyticsConfig'
import { useUrlByUsage } from 'utils/files'
import { testProps } from 'utils/test/qaData'

import { useLabeledGraphicContext, useLabeledGraphicImgRef } from '../LabeledGraphicContext'
import PointButton from '../styled/PointButton'
import PointStyled from '../styled/PointStyled'
import PointWrapperStyled from '../styled/PointWrapperStyled'
import RingStyled from '../styled/RingStyled'
import RingsStyled from '../styled/RingsStyled'
import * as s from './Point.module.scss'
import PointTooltip from './PointTooltip'

// TODO: move to tooltip default animation
const ANIMATION_VARIANTS = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1 },
} as const
const TRANSITION = { duration: 0.3 }
const EXIT = { opacity: 0, scale: 0.95 }

const toPercents = (full: number) => (value: number) => (value / full) * 100
const setX = (i: number, x: string) =>
  R.assocPath<string, LabeledGraphicElementValue>(['items', i, 'x'], x)
const setY = (i: number, y: string) =>
  R.assocPath<string, LabeledGraphicElementValue>(['items', i, 'y'], y)

const getContainer = (isPreview: boolean) =>
  document.getElementById(isPreview ? COURSE_TOOLTIP : COURSE_TOOLTIP_TEST_MATCHING) ||
  document.body

interface IPointProps {
  point: LabeledGraphicPoint
  index: number
  isActive: boolean
  value: LabeledGraphicElementValue
  id: string
  isAnimated?: boolean
  font: ElementFontCss<LabeledGraphicFontSchemaType>
}

const Point: React.FC<IPointProps> = ({ point, index, isActive, value, id, isAnimated, font }) => {
  const { type = 'image' } = value
  const styles = useLabeledGraphicContext((c) => c.styles)
  const mode = useLabeledGraphicContext((c) => c.mode)
  const isFill = mode.editorMode === AppMode.fill
  const element = useLabeledGraphicContext((c) => c.element)
  const onChange = useLabeledGraphicContext((c) => c.onChange)
  const imgRef = useLabeledGraphicImgRef()
  const setActivePoint = useLabeledGraphicContext((c) => c.setActivePoint)
  const activePoint = useLabeledGraphicContext((c) => c.activePoint)
  const { uuid } = useLabeledGraphicContext((c) => c.block) || {}
  const { entityAnalyticsConfig, sendXapiData, baseObjectExtensions } = useGetEntityAnalyticsConfig(
    {
      blockId: uuid || '',
      elementType: CONFIG_ELEMENTS.labeledGraphic.type,
      elementId: id,
    },
  )

  const ref = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const isEditor = mode.previewMode === PreviewMode.editor
  const isSkeleton = mode.previewMode === PreviewMode.skeleton
  const isImage = type === 'image'
  const isMobile = mode.deviceMode === 'mobile'
  const count = value.items.length

  const fileUrl = useUrlByUsage(point.pointIcon, FileUsageTypeEnum.image)
  const [isDragging, setIsDragging] = useState(false)

  const iconClasses = cn(s.icon, { [s.drag]: isDragging })
  const dragIconClasses = cn(s.dragIcon, { [s.drag]: isDragging })

  const animate = styles.animation.fade
    ? !isSkeleton && isActive
      ? 'visible'
      : 'hidden'
    : undefined

  const Component: Record<string, JSX.Element | null> = {
    number: (
      <div className={cn(isDragging && s.hidden)} style={font?.numbering}>
        {index + 1}
      </div>
    ),
    image:
      !styles.point.backgroundImage && !fileUrl ? (
        <div className={iconClasses}>
          <Icon name='otherAdd' />
        </div>
      ) : null,
    withoutIcon: null,
  }

  const onMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation()
    const { clientX, clientY } = e
    const clickStart = { x: clientX, y: clientY }
    let isDragDistance = false
    setActivePoint(null)

    const onMouseMove = (event: MouseEvent) => {
      isDragDistance =
        isDragDistance ||
        Math.abs(event.clientX - clickStart.x) > 5 ||
        Math.abs(event.clientY - clickStart.y) > 5
      if (isFill && isDragDistance) {
        setIsDragging(true)
        const { clientX, clientY } = event
        const img = imgRef?.current?.getBoundingClientRect()
        const { width = 900, height = 300, x: imgX = 0, y: imgY = 0 } = img || {}
        const percentsW = toPercents(width)
        const percentsH = toPercents(height)

        const point = ref.current?.getBoundingClientRect()
        const { width: pointWidth = 32, height: pointHeight = 32 } = point || {}

        const minX = 0 + percentsW(pointWidth) / 2
        const maxX = 100 - percentsW(pointWidth) / 2
        const x = R.clamp(minX, maxX, percentsW(clientX - imgX))

        const minY = 0 + percentsH(pointHeight) / 2
        const maxY = 100 - percentsH(pointHeight) / 2
        const y = R.clamp(minY, maxY, percentsH(clientY - imgY))
        onChange?.(R.compose(setX(index, `${x}%`), setY(index, `${y}%`))(value))
      }
    }

    const onMouseUp = () => {
      if (isDragDistance) {
        setIsDragging(false)
      } else {
        setActivePoint(isActive ? null : point.value)
      }

      document.removeEventListener('mousemove', onMouseMove, true)
      document.removeEventListener('mouseup', onMouseUp, true)
    }

    document.addEventListener('mousemove', onMouseMove, true)
    document.addEventListener('mouseup', onMouseUp, true)
  }

  const handleClose = () => {
    setActivePoint(null)
  }

  const PointInner = (
    <PointWrapperStyled $styles={styles}>
      {isAnimated && (
        <RingsStyled $styles={styles}>
          <RingStyled $styles={styles} />
          <RingStyled $styles={styles} />
        </RingsStyled>
      )}
      <PointStyled
        {...testProps({
          el: 'labeledGraphicElPoint',
          value: point.value,
          label: rtValueToText(point.description),
          index,
        })}
        $active={isActive}
        $editor={isEditor}
        $isImage={isImage}
        $path={fileUrl || point.pointIcon?.url}
        $styles={styles}
        ref={ref}
      >
        {type && Component[type]}
        <div className={dragIconClasses}>
          <Icon name='builderDrug' />
        </div>
      </PointStyled>
    </PointWrapperStyled>
  )

  return (
    <div
      className={s.root}
      onClick={() => {
        const data = entityAnalyticsConfig.opened({
          resultExtensions: {
            toLabelId: point.value,
            fromLabelId: null,
            total: count,
            current: index + 1,
          },
          objectExtensions: baseObjectExtensions,
          elementId: id,
        })
        sendXapiData(data)
      }}
      style={{ top: point.y, left: point.x }}
    >
      {isMobile && !isEditor ? (
        <div>{PointInner}</div>
      ) : (
        <DropdownCourse
          animate={animate}
          container={getContainer(mode.previewMode === PreviewMode.preview)}
          exit={EXIT}
          initial='hidden'
          key={index}
          name={String(index)}
          offsets={{ y: 15 }}
          onVisibleChange={(visible: boolean) => {
            if (!visible && !isEditor) {
              const data = entityAnalyticsConfig.closed({
                resultExtensions: {
                  toLabelId: null,
                  fromLabelId: activePoint || '',
                  total: count,
                  current: index + 1,
                },
                objectExtensions: baseObjectExtensions,
                elementId: id,
              })
              sendXapiData(data)
            }
            return !isEditor && setActivePoint(isActive ? null : point.value)
          }}
          overlay={
            <PointTooltip
              count={count}
              focusedElement={buttonRef.current}
              font={font}
              id={id}
              index={index}
              items={element.value.items}
              onClose={handleClose}
              point={point}
              value={value}
            />
          }
          overlayStyle={{ borderRadius: 'var(--brand-borderRadius)' }}
          placement='bottom'
          styleType='clear'
          transition={TRANSITION}
          variants={ANIMATION_VARIANTS}
          visible={!isSkeleton && isActive}
          flip
          shift
        >
          <PointButton aria-label={`Point ${index + 1} of ${count}`} onMouseDown={onMouseDown} ref={buttonRef}>
            {PointInner}
          </PointButton>
        </DropdownCourse>
      )}
    </div>
  )
}

export default Point
